"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { getB2BLanguageSwitchConfig } from "./b2bLanguageSwitch";

const LANGUAGE_METADATA: Record<string, { code: string; menuLabel: string }> = {
  en: { code: "EN", menuLabel: "English - EN" },
  hindi: { code: "HI", menuLabel: "हिंदी - HI (BETA)" },
};

const B2BLanguageSwitcher = ({ enabled = false }: { enabled?: boolean }) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const switchConfig = useMemo(() => {
    if (!enabled || !pathname) {
      return null;
    }

    const queryString = searchParams?.toString();
    const search = queryString ? `?${queryString}` : "";
    return getB2BLanguageSwitchConfig(pathname, search);
  }, [enabled, pathname, searchParams]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  if (!switchConfig) {
    return null;
  }

  const currentOption = switchConfig.options.find(
    ({ value }) => value === switchConfig.currentLanguage
  );
  const currentLanguageLabel = currentOption?.label ?? "English";

  return (
    <div
      ref={menuRef}
      data-testid="b2b-language-switcher"
      className="group relative flex items-center"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        aria-label="Select page language"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="flex h-[68px] w-10 cursor-pointer flex-col items-center justify-center gap-[5px] text-xs leading-[14px] font-medium text-white transition-colors md:h-auto md:w-auto md:gap-1 md:px-3 md:pt-[14px] md:pb-[30px] md:hover:bg-[#001326]"
        onClick={() => setIsOpen((open) => !open)}
      >
        <i
          aria-hidden="true"
          className="bf-icon-vernac-language relative -top-2 inline-flex h-6 w-6 items-center justify-center text-2xl leading-none md:static"
        />
        <span className="hidden items-center text-[12px] leading-[14px] whitespace-nowrap md:flex">
          <span>{currentLanguageLabel}</span>
          <i
            aria-hidden="true"
            className={`icomoon ml-[2px] text-[10px] transition-transform duration-300 before:content-['\\e981'] ${isOpen ? "-rotate-180" : "group-hover:-rotate-180"}`}
          />
        </span>
      </button>

      {isOpen ? (
        <div
          role="menu"
          aria-label="Language options"
          className="absolute top-[40px] left-0 z-20 min-w-[158px] overflow-hidden rounded-[8px] bg-white py-2 text-gray-900 shadow-lg md:top-full md:min-w-[246px] md:rounded-t-none md:rounded-b-2xl md:bg-[#03162b] md:text-white md:shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
        >
          {switchConfig.options.map(({ label, value, href }) => {
            const metadata = LANGUAGE_METADATA[value] ?? {
              code: value.toUpperCase(),
              menuLabel: `${label} - ${value.toUpperCase()}`,
            };
            const isSelected = value === switchConfig.currentLanguage;

            return (
              <button
                key={value}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                className="flex w-full cursor-pointer items-center gap-3 px-4 py-[10px] text-left text-[12px] leading-[14px] font-medium text-gray-900 transition-colors hover:bg-black/5 md:gap-4 md:px-6 md:py-4 md:text-white md:hover:bg-white/5"
                onClick={() => {
                  setIsOpen(false);
                  if (!isSelected) {
                    router.push(href);
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                    isSelected ? "border-[#ff7a00]" : "border-gray-500 bg-white md:border-white"
                  }`}
                >
                  {isSelected ? (
                    <span className="block h-[6px] w-[6px] rounded-full bg-[#ff7a00]" />
                  ) : null}
                </span>
                <span>{metadata.menuLabel}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default B2BLanguageSwitcher;
